import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { EnrollmentStatus, PortfolioMovementType } from './portfolio-movement.service';

// === Enums (reutilizando los existentes de portfolio-movement.service) ===

export enum PayerType {
  PARTICIPANT = 1,
  COMPANY = 2,
  PARTICIPANT_CORPORATE = 3
}

// === Interfaces para DTOs Corporativos ===

export interface CorporateParticipantInfo {
  participantId: number;
  participantName: string;
  participantDocument: string;
  coverageType: string;
  companyCoveragePercent?: number;
  companyCoverageAmount?: number;
  pendingInstallments: number;
  pendingAmount: number;
}

export interface CorporatePendingInstallment {
  corporatePaymentPlanId: number;
  installmentNumber: number;
  dueDate: Date;
  amountToPay: number;
  amountPaid: number;
  pendingAmount: number;
  paymentStatus: string;
  paymentStatusDescription: string;
  payerType: PayerType;
  payerTypeDescription: string;
  participantId?: number;
  participantName?: string;
  isPunished: boolean;
  movementType: PortfolioMovementType;
  isOverdue: boolean;
  daysOverdue: number;
}

export interface CorporateEnrollmentWithdrawalInfo {
  corporateEnrollmentId: number;
  status: EnrollmentStatus;
  statusDescription: string;
  companyId: number;
  companyName: string;
  companyNit: string;
  projectId: number;
  projectName: string;
  projectMnemonic: string;
  defaultCompanyCoveragePercent: number;
  isFixedCoverage: boolean;
  isInternational: boolean;
  currencyCode?: string;
  currencySymbol?: string;
  totalCompanyAmount: number;
  totalParticipantsAmount: number;
  totalPendingAmount: number;
  totalCompanyInstallments: number;
  paidCompanyInstallments: number;
  pendingCompanyInstallments: number;
  totalParticipantInstallments: number;
  paidParticipantInstallments: number;
  pendingParticipantInstallments: number;
  totalParticipants: number;
  participants: CorporateParticipantInfo[];
  companyPendingPlans: CorporatePendingInstallment[];
  participantPendingPlans: CorporatePendingInstallment[];
  canBeWithdrawn: boolean;
  canBeDeferred: boolean;
  canBeReversed: boolean;
  canContinueFromDeferral: boolean;
  originCorporateEnrollmentId?: number;
  withdrawalReason?: string;
  lastPortfolioMovementDate?: Date;
}

export interface WithdrawCorporateEnrollmentRequest {
  corporateEnrollmentId: number;
  reason: string;
  corporatePaymentPlanIds?: number[];
  notes?: string;
}

export interface ReverseCorporateWithdrawalRequest {
  corporateEnrollmentId: number;
  continueInSameProject: boolean;
  newProjectId?: number;
  reason: string;
  notes?: string;
  // Nuevos campos para valores personalizados
  newTotalValueCOP?: number;
  newTotalValueUSD?: number;
  currencyId?: number;
  newPaymentPlan?: { installmentNumber: number; dueDate: Date; amount: number; }[];
}

export interface DeferCorporateEnrollmentRequest {
  corporateEnrollmentId: number;
  reason: string;
  corporatePaymentPlanIds?: number[];
  notes?: string;
}

export interface ContinueFromCorporateDeferralRequest {
  corporateEnrollmentId: number;
  continueInSameProject: boolean;
  newProjectId?: number;
  reason: string;
  notes?: string;
  newTotalValue?: number;
  newPaymentPlan?: { installmentNumber: number; dueDate: Date; amount: number; }[];
}

export interface CorporatePortfolioMovementResult {
  success: boolean;
  message: string;
  movementId?: number;
  corporateEnrollmentId: number;
  newCorporateEnrollmentStatus: EnrollmentStatus;
  newCorporateEnrollmentId?: number;
  affectedInstallmentsCount: number;
  totalAffectedAmount: number;
  affectedCorporatePaymentPlanIds: number[];
  migratedCorporatePaymentPlanIds: number[];
  companyInstallmentsAffected: number;
  participantInstallmentsAffected: number;
  companyAmountAffected: number;
  participantAmountAffected: number;
}

export interface CorporatePortfolioMovementResponse {
  id: number;
  corporateEnrollmentId: number;
  movementType: PortfolioMovementType;
  movementTypeDescription: string;
  movementDate: Date;
  reason?: string;
  targetCorporateEnrollmentId?: number;
  corporatePaymentPlanIdsJson?: string;
  performedBy: string;
  totalAffectedAmount: number;
  affectedInstallmentsCount: number;
  notes?: string;
  isReverted: boolean;
  revertedByMovementId?: number;
  companyName?: string;
  projectName?: string;
  targetProjectName?: string;
}

export interface CorporatePortfolioMovementStats {
  totalWithdrawals: number;
  totalReversals: number;
  totalDeferrals: number;
  totalWithdrawnAmount: number;
  totalReversedAmount: number;
  totalDeferredAmount: number;
  activeWithdrawals: number;
  activeDeferrals: number;
}

@Injectable({
  providedIn: 'root'
})
export class CorporatePortfolioMovementService {
  private _baseUrl: string = "";
  private _controllerName: string = "";

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
    this._controllerName = "CorporatePortfolioMovement";
  }

  // === Métodos de consulta ===

  /**
   * Obtener información de un acuerdo corporativo para el wizard
   */
  getCorporateEnrollmentWithdrawalInfo(corporateEnrollmentId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/enrollment-info/${corporateEnrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener acuerdos corporativos activos de una empresa
   */
  getActiveCorporateEnrollmentsByCompany(companyId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/company/${companyId}/active`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener acuerdos corporativos retirados de una empresa
   */
  getWithdrawnCorporateEnrollmentsByCompany(companyId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/company/${companyId}/withdrawn`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener acuerdos corporativos aplazados de una empresa
   */
  getDeferredCorporateEnrollmentsByCompany(companyId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/company/${companyId}/deferred`)
      .pipe(catchError(this.handleErrors));
  }

  // === Métodos de acción ===

  /**
   * Procesar retiro de acuerdo corporativo
   */
  withdrawCorporateEnrollment(request: WithdrawCorporateEnrollmentRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/withdraw`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Revertir retiro de acuerdo corporativo
   */
  reverseCorporateWithdrawal(request: ReverseCorporateWithdrawalRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/reverse-withdrawal`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Procesar aplazamiento de acuerdo corporativo
   */
  deferCorporateEnrollment(request: DeferCorporateEnrollmentRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/defer`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Continuar desde aplazamiento creando nuevo acuerdo
   */
  continueFromCorporateDeferral(request: ContinueFromCorporateDeferralRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/continue-from-deferral`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Castigar cuotas corporativas manualmente
   */
  punishCorporateInstallments(request: { corporateEnrollmentId: number, corporatePaymentPlanIds: number[], reason: string, notes?: string }): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/punish-installments`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Descastigar cuotas corporativas manualmente
   */
  unpunishCorporateInstallments(request: { corporateEnrollmentId: number, corporatePaymentPlanIds: number[], reason: string, notes?: string }): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/unpunish-installments`, request)
      .pipe(catchError(this.handleErrors));
  }

  // === Métodos de listado paginado ===

  /**
   * Obtener movimientos corporativos paginados (listado global)
   */
  getCorporateMovementsPaged(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.pageNumber) httpParams = httpParams.set('pageNumber', params.pageNumber.toString());
      if (params.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
      if (params.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);
      if (params.movementType !== null && params.movementType !== undefined && params.movementType !== '')
        httpParams = httpParams.set('movementType', params.movementType.toString());
      if (params.projectId) httpParams = httpParams.set('projectId', params.projectId.toString());
      if (params.companyId) httpParams = httpParams.set('companyId', params.companyId.toString());
      if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
      if (params.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
    }
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/list`, { params: httpParams })
      .pipe(catchError(this.handleErrors));
  }

  // === Métodos de historial y estadísticas ===

  /**
   * Obtener historial de movimientos de un acuerdo corporativo
   */
  getCorporateMovementHistory(corporateEnrollmentId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/history/${corporateEnrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener estadísticas de movimientos corporativos
   */
  getCorporateMovementStats(projectId?: number, companyId?: number, fromDate?: Date, toDate?: Date): Observable<any> {
    let params = new HttpParams();
    if (projectId) params = params.set('projectId', projectId.toString());
    if (companyId) params = params.set('companyId', companyId.toString());
    if (fromDate) params = params.set('fromDate', fromDate.toISOString());
    if (toDate) params = params.set('toDate', toDate.toISOString());

    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/stats`, { params })
      .pipe(catchError(this.handleErrors));
  }

  // === Helpers ===

  /**
   * Obtener descripción del tipo de pagador
   */
  static getPayerTypeDescription(payerType: PayerType): string {
    switch (payerType) {
      case PayerType.COMPANY: return 'Empresa';
      case PayerType.PARTICIPANT: return 'Participante';
      case PayerType.PARTICIPANT_CORPORATE: return 'Participante Corporativo';
      default: return 'Desconocido';
    }
  }

  /**
   * Calcular resumen de cuotas por tipo de pagador
   */
  static calculatePaymentSummary(enrollment: CorporateEnrollmentWithdrawalInfo): {
    companyPending: number;
    companyAmount: number;
    participantsPending: number;
    participantsAmount: number;
  } {
    return {
      companyPending: enrollment.pendingCompanyInstallments,
      companyAmount: enrollment.companyPendingPlans
        .filter(p => !p.isPunished)
        .reduce((sum, p) => sum + p.pendingAmount, 0),
      participantsPending: enrollment.pendingParticipantInstallments,
      participantsAmount: enrollment.participantPendingPlans
        .filter(p => !p.isPunished)
        .reduce((sum, p) => sum + p.pendingAmount, 0)
    };
  }

  private handleErrors(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.log("Ocurrió un error", error.error.message);
    } else {
      console.log(`Ocurrió un error ${error.status}, ${error.error}`);
    }
    return throwError(() => new Error('Algo salió mal; por favor, intente nuevamente más tarde.'));
  }
}

